import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    List,
    ListItem,
    ListItemText,
    Paper,
    CircularProgress
} from '@mui/material';
import { FindInPage, Warning } from '@mui/icons-material';
import PropTypes from 'prop-types';

const TranslationMonitoringDialog = ({ 
    open, 
    onClose, 
    loading, 
    data, 
    onFixInEditor 
}) => {
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <FindInPage />
                    Translation Status Check
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                        <CircularProgress />
                        <Typography variant="body1" sx={{ ml: 2 }}>
                            Checking translations...
                        </Typography>
                    </Box>
                ) : data ? (
                    <Box>
                        {data.error ? (
                            <Alert severity={data.noTranslation ? "info" : "error"} sx={{ mb: 2 }}>
                                {data.error}
                                {data.noTranslation && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        To check for missing translations, you must first create a translation for this story using the Translation Editor.
                                    </Typography>
                                )}
                            </Alert>
                        ) : (
                            <>
                                {!data.hasUntranslated ? (
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            All Good! 🎉
                                        </Typography>
                                        <Typography variant="body2">
                                            All words and phrases have translations. This story is ready to be served to users.
                                        </Typography>
                                    </Alert>
                                ) : (
                                    <>
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            <Typography variant="h6" gutterBottom>
                                                <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                Missing Translations Found
                                            </Typography>
                                            <Typography variant="body2">
                                                Found {data.summary?.totalUntranslated || 0} untranslated items that need attention before serving to users.
                                            </Typography>
                                        </Alert>

                                        {data.untranslatedWords?.length > 0 && (
                                            <Box mb={3}>
                                                <Typography variant="h6" gutterBottom color="error">
                                                    Untranslated Words ({data.untranslatedWords.length})
                                                </Typography>
                                                <Paper elevation={1} sx={{ maxHeight: 200, overflow: 'auto' }}>
                                                    <List dense>
                                                        {data.untranslatedWords.map((word, index) => (
                                                            <ListItem key={index}>
                                                                <ListItemText
                                                                    primary={word.word}
                                                                    secondary={`Lemma: ${word.lemma || 'N/A'} | POS: ${word.pos || 'N/A'}`}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Paper>
                                            </Box>
                                        )}

                                        {data.untranslatedPhrases?.length > 0 && (
                                            <Box mb={3}>
                                                <Typography variant="h6" gutterBottom color="error">
                                                    Untranslated Phrases ({data.untranslatedPhrases.length})
                                                </Typography>
                                                <Paper elevation={1} sx={{ maxHeight: 200, overflow: 'auto' }}>
                                                    <List dense>
                                                        {data.untranslatedPhrases.map((phrase, index) => (
                                                            <ListItem key={index}>
                                                                <ListItemText
                                                                    primary={phrase.displayPhrase || phrase.phrase}
                                                                    secondary={`ID: ${phrase.id}`}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Paper>
                                            </Box>
                                        )}
                                    </>
                                )}

                                <Box mt={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Word Objects: {data.totalWordObjects || 0} |
                                        Dictionary Entries: {data.totalDictionaryEntries || 0} |
                                        Phrases: {data.totalPhrases || 0}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>
                {onFixInEditor && (data?.hasUntranslated || data?.noTranslation) && (
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => {
                            onClose();
                            onFixInEditor();
                        }}
                    >
                        {data?.noTranslation ? 'Create Translation' : 'Fix in Translation Editor'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

TranslationMonitoringDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    data: PropTypes.object,
    onFixInEditor: PropTypes.func
};

export default TranslationMonitoringDialog;
